import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentAPI } from '../services/api';
import { FiDollarSign, FiRefreshCw, FiCalendar, FiCheckCircle, FiClock, FiXCircle, FiMail, FiPhone, FiBook } from 'react-icons/fi';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, success, pending, failed
  const [dateRange, setDateRange] = useState({
    from: Math.floor(Date.now() / 1000) - (30 * 24 * 60 * 60), // Last 30 days
    to: Math.floor(Date.now() / 1000)
  });
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    pending: 0,
    failed: 0,
    totalAmount: 0,
    successAmount: 0
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.getTransactions({
        from: dateRange.from,
        to: dateRange.to,
        status: filter === 'all' ? undefined : filter
      });
      
      if (response.data.success) {
        setTransactions(response.data.data);
        calculateStats(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch transactions');
      }
    } catch (error) {
      toast.error('Error fetching transactions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      successful: data.filter(t => t.status === 'captured' || t.status === 'success').length,
      pending: data.filter(t => t.status === 'pending' || t.status === 'authorized').length,
      failed: data.filter(t => t.status === 'failed').length,
      totalAmount: data.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0),
      successAmount: data.filter(t => t.status === 'captured' || t.status === 'success').reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
    };
    setStats(stats);
  };

  const handleDateRangeChange = (days) => {
    const to = Math.floor(Date.now() / 1000);
    const from = to - (days * 24 * 60 * 60);
    setDateRange({ from, to });
  };

  const handleRefresh = () => {
    fetchTransactions();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      captured: { color: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Success' },
      success: { color: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Success' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FiClock, label: 'Pending' },
      authorized: { color: 'bg-blue-100 text-blue-800', icon: FiClock, label: 'Authorized' },
      failed: { color: 'bg-red-100 text-red-800', icon: FiXCircle, label: 'Failed' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: FiClock, label: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const filteredTransactions = filter === 'all' 
    ? transactions 
    : transactions.filter(t => {
        if (filter === 'success') return t.status === 'captured' || t.status === 'success';
        if (filter === 'pending') return t.status === 'pending' || t.status === 'authorized';
        return t.status === filter;
      });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Transactions</h1>
          <p className="text-gray-600 mt-1">View and manage all payment transactions</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiDollarSign className="text-2xl text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCheckCircle className="text-2xl text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-gray-900">{stats.successful}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FiClock className="text-2xl text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <FiXCircle className="text-2xl text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.failed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FiCalendar className="text-gray-600" />
          <span className="text-sm text-gray-600 font-medium">Date Range:</span>
          <button
            onClick={() => { handleDateRangeChange(7); fetchTransactions(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(30); fetchTransactions(); }}
            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg"
          >
            Last 30 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(90); fetchTransactions(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 90 Days
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 font-medium">Status:</span>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'all' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('success')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'success' ? 'bg-green-100 text-green-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Successful
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('failed')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              filter === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Failed
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <FiDollarSign className="mx-auto text-5xl text-gray-400 mb-4" />
            <p className="text-gray-600">No transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{transaction.payment_id || transaction.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {transaction.email && (
                          <div className="flex items-center gap-1 text-gray-900">
                            <FiMail className="text-gray-400" />
                            {transaction.email}
                          </div>
                        )}
                        {transaction.contact && (
                          <div className="flex items-center gap-1 text-gray-600 mt-1">
                            <FiPhone className="text-gray-400" />
                            {transaction.contact}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FiBook className="text-gray-400" />
                        <span className="text-sm text-gray-900">{transaction.course_title || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ₹{parseFloat(transaction.amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded capitalize">
                        {transaction.method || 'online'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transaction.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(transaction.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">₹{stats.totalAmount.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Successful Revenue</p>
            <p className="text-2xl font-bold text-green-600">₹{stats.successAmount.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Success Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.total > 0 ? ((stats.successful / stats.total) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
